import {
  DEFAULT_REACTION_KIND,
  REACTION_OPTIONS,
  getReactionEmoji,
  getReactionOption,
  getReactionPalette,
  normalizeReactionKind,
} from "./reactionKinds";

export const CUSTOM_REACTION_KIND = "CUSTOM";

export const REACTION_TARGET_TYPES = Object.freeze({
  CHAT_MESSAGE: "CHAT_MESSAGE",
  FORUM_TOPIC: "FORUM_TOPIC",
  FORUM_COMMENT: "FORUM_COMMENT",
  BLOG_POST: "BLOG_POST",
  BLOG_COMMENT: "BLOG_COMMENT",
  PODCAST_EPISODE: "PODCAST_EPISODE",
  PODCAST_COMMENT: "PODCAST_COMMENT",
  LITERATURE_BOOK: "LITERATURE_BOOK",
  LITERATURE_COMMENT: "LITERATURE_COMMENT",
  PSYCHOLOGIST_STORY: "PSYCHOLOGIST_STORY",
});

const KIND_ALIASES = {
  HEART: "LIKE",
  LOVE: "LIKE",
  LIKE_REACTION: "LIKE",
  FIRE_REACTION: "FIRE",
  LIGHTBULB: "INSIGHT",
  IDEA: "INSIGHT",
  BRAIN: "SUPPORT",
  HUG: "SUPPORT",
  SUPPORTIVE: "SUPPORT",
  CLAP: "APPLAUSE",
  HANDSHAKE: "APPLAUSE",
  FAVORITE: "STAR",
  FAVOURITE: "STAR",
  BOOKMARK: "STAR",
};

const OPTION_BY_EMOJI = REACTION_OPTIONS.reduce((acc, item) => {
  acc[item.emoji] = item;
  return acc;
}, {});

const EMOJI_ALIASES = {
  "❤": "❤️",
  "♥": "❤️",
  "💖": "❤️",
  "👏🏻": "👏",
  "👏🏼": "👏",
  "👏🏽": "👏",
  "👏🏾": "👏",
  "👏🏿": "👏",
  "🥺": "🙌",
  "🤗": "🙌",
  "🧠": "🙌",
  "⭐️": "⭐",
};

function cleanString(value) {
  return String(value ?? "").trim();
}

function normalizeEmoji(value) {
  const raw = cleanString(value);
  if (!raw) return "";
  return EMOJI_ALIASES[raw] || raw;
}

export function normalizeReactionValue(value, fallbackKind = DEFAULT_REACTION_KIND) {
  const raw = cleanString(value);
  const fallback = fallbackKind === "" || fallbackKind === null ? null : normalizeReactionKind(fallbackKind, DEFAULT_REACTION_KIND);
  if (!raw) {
    if (!fallback) {
      return {
        kind: "",
        reactionKind: "",
        reactionType: "",
        type: "",
        emoji: "",
        reaction: "",
        label: "",
        colors: [],
        known: false,
      };
    }
    const kind = fallback || DEFAULT_REACTION_KIND;
    const option = getReactionOption(kind);
    return {
      kind,
      reactionKind: kind,
      reactionType: kind,
      type: kind,
      emoji: option.emoji,
      reaction: option.emoji,
      label: option.label,
      colors: getReactionPalette(kind),
      known: true,
    };
  }

  const emoji = normalizeEmoji(raw);
  const byEmoji = OPTION_BY_EMOJI[emoji];
  if (byEmoji) {
    return {
      kind: byEmoji.kind,
      reactionKind: byEmoji.kind,
      reactionType: byEmoji.kind,
      type: byEmoji.kind,
      emoji: byEmoji.emoji,
      reaction: byEmoji.emoji,
      label: byEmoji.label,
      colors: [...byEmoji.colors],
      known: true,
    };
  }

  const upper = raw.toUpperCase();
  const aliased = KIND_ALIASES[upper] || upper;
  const normalizedKind = normalizeReactionKind(aliased, null);
  if (normalizedKind) {
    const option = getReactionOption(normalizedKind);
    return {
      kind: normalizedKind,
      reactionKind: normalizedKind,
      reactionType: normalizedKind,
      type: normalizedKind,
      emoji: option.emoji,
      reaction: option.emoji,
      label: option.label,
      colors: getReactionPalette(normalizedKind),
      known: true,
    };
  }

  // Chat can use any emoji, while catalog/forum/literature/podcast reactions use the known kind.
  return {
    kind: CUSTOM_REACTION_KIND,
    reactionKind: CUSTOM_REACTION_KIND,
    reactionType: CUSTOM_REACTION_KIND,
    type: CUSTOM_REACTION_KIND,
    emoji,
    reaction: emoji,
    label: emoji,
    colors: getReactionPalette(fallback || DEFAULT_REACTION_KIND),
    known: false,
  };
}

export function normalizeReactionKindForApi(value, fallback = DEFAULT_REACTION_KIND) {
  const normalized = normalizeReactionValue(value, fallback);
  return normalized.kind === CUSTOM_REACTION_KIND
    ? normalizeReactionKind(fallback, DEFAULT_REACTION_KIND)
    : normalized.kind;
}

export function getReactionBurstPayload(value, fallback = DEFAULT_REACTION_KIND) {
  const normalized = normalizeReactionValue(value, fallback);
  const knownKind = normalized.kind === CUSTOM_REACTION_KIND
    ? normalizeReactionKind(fallback, DEFAULT_REACTION_KIND)
    : normalized.kind;
  return {
    kind: knownKind,
    emoji: normalized.emoji || getReactionEmoji(knownKind),
    colors: normalized.colors?.length ? [...normalized.colors] : getReactionPalette(knownKind),
  };
}

export function buildReactionTogglePayload({
  reaction,
  kind,
  reactionKind,
  reactionType,
  emoji,
  add,
  targetType,
  targetId,
  actorId,
  clientId,
  extra = null,
} = {}) {
  const source = reaction ?? kind ?? reactionKind ?? reactionType ?? emoji ?? DEFAULT_REACTION_KIND;
  const normalized = normalizeReactionValue(source, DEFAULT_REACTION_KIND);
  const apiKind = normalized.kind === CUSTOM_REACTION_KIND ? DEFAULT_REACTION_KIND : normalized.kind;
  const payload = {
    kind: apiKind,
    reactionKind: apiKind,
    reactionType: apiKind,
    type: apiKind,
    emoji: normalized.emoji || getReactionEmoji(apiKind),
    reaction: normalized.emoji || getReactionEmoji(apiKind),
  };

  if (add !== undefined && add !== null) payload.add = Boolean(add);
  if (targetType) payload.targetType = String(targetType);
  if (targetId !== undefined && targetId !== null) payload.targetId = targetId;
  if (actorId !== undefined && actorId !== null) payload.actorId = actorId;
  if (clientId !== undefined && clientId !== null) payload.clientId = String(clientId);
  if (extra && typeof extra === "object") Object.assign(payload, extra);

  return payload;
}

export function buildReactionQuery(params = {}) {
  const payload = buildReactionTogglePayload(params);
  const query = new URLSearchParams();
  query.set("kind", payload.kind);
  query.set("reactionKind", payload.reactionKind);
  query.set("reactionType", payload.reactionType);
  query.set("type", payload.type);
  query.set("emoji", payload.emoji);
  if (payload.add !== undefined) query.set("add", String(payload.add));
  return query.toString();
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeReactionResponse(raw, fallbackKind = DEFAULT_REACTION_KIND) {
  const data = asObject(raw?.item ?? raw?.items ?? raw);
  const activeRaw =
    data.myReactionKind ??
    data.reactionKindByMe ??
    data.myReactionType ??
    data.reactionType ??
    data.myReaction ??
    data.viewerReaction ??
    data.selectedReaction ??
    data.type ??
    data.kind ??
    null;
  const activeKind = normalizeReactionKindForApi(activeRaw, "") ||
    (data.liked || data.active || data.reacted || data.selected || data.mine || data.me ? normalizeReactionKindForApi(fallbackKind) : "");
  const reactionsCount = Math.max(
    0,
    toNumber(data.reactionsCount ?? data.count ?? data.likesCount ?? data.total ?? data.value, 0)
  );

  return {
    liked: Boolean(data.liked || data.active || data.reacted || data.selected || data.mine || data.me || activeKind),
    reacted: Boolean(data.liked || data.active || data.reacted || data.selected || data.mine || data.me || activeKind),
    reactionsCount,
    myReactionKind: activeKind || null,
    reactionKind: activeKind || null,
    reactionType: activeKind || null,
    emoji: activeKind ? getReactionEmoji(activeKind) : getReactionEmoji(fallbackKind),
    raw: data,
  };
}

export function normalizeReactionState(raw = {}, fallbackKind = DEFAULT_REACTION_KIND) {
  const data = asObject(raw?.item ?? raw?.items ?? raw);
  const counts = {};
  let activeType = normalizeReactionKindForApi(
    data.myReactionKind ?? data.myReactionType ?? data.myReaction ?? data.viewerReaction ?? data.selectedReaction ?? "",
    ""
  );

  const addCount = (typeValue, countValue, active = false) => {
    const normalized = normalizeReactionKindForApi(typeValue, "");
    if (!normalized) return;
    counts[normalized] = Math.max(0, toNumber(counts[normalized], 0) + Math.max(0, toNumber(countValue, 0)));
    if (!activeType && active) activeType = normalized;
  };

  const candidates = [
    data.reactionStats,
    data.reactions,
    data.reactionCounts,
    data.stats?.reactions,
    data.counts,
  ];

  candidates.forEach((candidate) => {
    if (Array.isArray(candidate)) {
      candidate.forEach((item) => {
        const entry = asObject(item);
        addCount(
          entry.kind ?? entry.reactionKind ?? entry.type ?? entry.reactionType ?? entry.code ?? entry.emoji,
          entry.count ?? entry.total ?? entry.value ?? entry.reactionsCount ?? entry.likesCount,
          Boolean(entry.mine || entry.me || entry.active || entry.selected)
        );
      });
      return;
    }
    if (candidate && typeof candidate === "object") {
      Object.entries(candidate).forEach(([key, value]) => {
        if (value && typeof value === "object") {
          const entry = asObject(value);
          addCount(
            entry.kind ?? entry.reactionKind ?? entry.type ?? entry.reactionType ?? entry.code ?? entry.emoji ?? key,
            entry.count ?? entry.total ?? entry.value ?? entry.reactionsCount ?? entry.likesCount,
            Boolean(entry.mine || entry.me || entry.active || entry.selected)
          );
        } else {
          addCount(key, value, false);
        }
      });
    }
  });

  if (!Object.keys(counts).length) {
    const fallbackCount = Math.max(0, toNumber(data.reactionsCount ?? data.likesCount ?? data.count, 0));
    if (fallbackCount || data.liked || data.active || data.reacted || activeType) {
      const kind = activeType || normalizeReactionKindForApi(fallbackKind);
      counts[kind] = fallbackCount;
    }
  }

  if (!activeType && (data.liked || data.active || data.reacted || data.selected || data.mine || data.me)) {
    activeType = normalizeReactionKindForApi(fallbackKind);
  }

  return { counts, activeType };
}

export function buildNextReactionState(currentState = {}, reaction = DEFAULT_REACTION_KIND) {
  const normalizedType = normalizeReactionKindForApi(reaction, DEFAULT_REACTION_KIND);
  const previousType = normalizeReactionKindForApi(currentState?.activeType, "");
  const nextCounts = { ...asObject(currentState?.counts) };

  if (previousType && previousType !== normalizedType) {
    nextCounts[previousType] = Math.max(0, toNumber(nextCounts[previousType], 0) - 1);
  }

  let nextActiveType = normalizedType;
  if (previousType === normalizedType) {
    nextActiveType = "";
    nextCounts[normalizedType] = Math.max(0, toNumber(nextCounts[normalizedType], 0) - 1);
  } else {
    nextCounts[normalizedType] = Math.max(0, toNumber(nextCounts[normalizedType], 0) + 1);
  }

  return { counts: nextCounts, activeType: nextActiveType };
}

export function buildReactionButtons(reactionState = {}, options = REACTION_OPTIONS) {
  const counts = asObject(reactionState?.counts);
  const activeType = normalizeReactionKindForApi(reactionState?.activeType, "");
  const known = new Set();
  const buttons = options.map((option) => {
    const kind = normalizeReactionKindForApi(option.kind, DEFAULT_REACTION_KIND);
    known.add(kind);
    return {
      ...option,
      type: kind,
      kind,
      count: Math.max(0, toNumber(counts[kind], 0)),
      active: activeType === kind,
    };
  });

  Object.entries(counts).forEach(([rawKind, count]) => {
    const kind = normalizeReactionKindForApi(rawKind, "");
    if (!kind || known.has(kind)) return;
    const burst = getReactionBurstPayload(kind);
    buttons.push({
      kind,
      type: kind,
      emoji: burst.emoji,
      label: kind,
      colors: burst.colors,
      count: Math.max(0, toNumber(count, 0)),
      active: activeType === kind,
    });
  });

  return buttons;
}
